import { Select } from '@hangar/web-components/core/Select';
import type { SelectItemProps } from '@hangar/web-components/core/shared/components/SelectItem';
import type { SelectOptionGroupProps } from '@hangar/web-components/core/shared/components/SelectOptionGroup';
import styled from 'styled-components';

const DISABLED_SURFACE = '#E0E0E0';
const DISABLED_TEXT = '#BDBDBD';

const FullWidth = styled.div<{ $inactive?: boolean }>`
  width: 100%;
  box-sizing: border-box;

  ${({ $inactive }) =>
    $inactive &&
    `
    opacity: 1;
    & label[for] {
      color: ${DISABLED_TEXT} !important;
    }
    & [role='combobox'] {
      cursor: not-allowed;
      background-color: ${DISABLED_SURFACE} !important;
      border: none !important;
      border-radius: 8px !important;
      box-shadow: none !important;
      color: ${DISABLED_TEXT} !important;
    }
    & [role='combobox'] svg {
      color: ${DISABLED_TEXT} !important;
    }
  `}
`;

export type HangarSelectOption = { value: string; label: string };

export type HangarSelectFieldProps = Readonly<{
  id: string;
  label: string;
  placeholder: string;
  value: string;
  options: HangarSelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  errorText?: string;
  helperText?: string;
  emptyText?: string;
  /**
   * Si es true (default), la primera opción es `value: ''` con `label` = `placeholder`.
   * Si es false, no hay fila vacía y el texto en reposo viene del `placeholder` nativo del Select (p. ej. filtros).
   */
  leadingEmptyOption?: boolean;
  'data-testid'?: string;
}>;

/**
 * Single-value Hangar Select. Por defecto incluye una opción vacía inicial para formularios;
 * con `leadingEmptyOption={false}` solo hay opciones reales y el reposo usa `placeholder` del Select.
 *
 * **`disabled` y `readOnly`:** ambos activan el estado inactivo (`isInactive`). El `Select` Hangar
 * recibe siempre `disabled={isInactive}` y `readOnly={false}`: el modo “solo lectura” de producto
 * se implementa como deshabilitado en el DS, no como `readOnly` nativo del `Select`.
 */
export function HangarSelectField({
  id,
  label,
  placeholder,
  value,
  options,
  onChange,
  disabled,
  readOnly,
  error,
  errorText,
  helperText,
  emptyText,
  leadingEmptyOption = true,
  'data-testid': dataTestId,
}: HangarSelectFieldProps) {
  const isInactive = Boolean(disabled || readOnly);
  const optionRows = options.map((o) => ({ value: o.value, label: o.label }));
  const items: SelectItemProps[] = leadingEmptyOption
    ? [{ value: '', label: placeholder }, ...optionRows]
    : optionRows;
  const groups: SelectOptionGroupProps[] = [{ options: items }];

  const handleChange = (item: SelectItemProps) => {
    onChange(item.value);
  };

  return (
    <FullWidth data-testid={dataTestId} $inactive={isInactive}>
      <Select
        id={id}
        label={label}
        placeholder={leadingEmptyOption ? '' : placeholder}
        value={value}
        options={groups}
        onChange={handleChange}
        disabled={isInactive}
        readOnly={false}
        error={error}
        errorText={errorText ?? ''}
        helperText={helperText ?? ''}
        emptyText={emptyText ?? ''}
      />
    </FullWidth>
  );
}
